import type { ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { Column, Row, Section } from "@react-email/components";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useGoober } from "../../../Design/GooberContext";
import { mobileMediaQuery } from "../../../Design/mediaQuery";
import { useTopBlockStyles } from "../../../Design/useTopBlockStyles";
import { SectionContainer } from "../../../Utils/SectionContainer";
import { defaultStyles } from "./defaultStyles";

export interface FeatureBlock3Props extends ComponentPropsWithRef<"table"> {
  footerContent?: ReactNode;
  headerContent?: ReactNode;
  section1Content?: ReactNode;
  section2Content?: ReactNode;
  section3Content?: ReactNode;
  styles?: Record<string, StyleValue>;
}

export const FeatureBlock3 = forwardRef<HTMLTableElement, FeatureBlock3Props>(
  (
    {
      headerContent,
      section1Content,
      section2Content,
      section3Content,
      footerContent,
      styles,
      ...rest
    },
    ref,
  ) => {
    const { css } = useGoober();
    const { backgroundStyles, paddingStyles, marginStyles } = useTopBlockStyles(
      {
        styles,
        defaultStyles,
      },
    );
    const columnStyle = { width: `${100 / 3}%`, verticalAlign: "top" } as const;

    return (
      <SectionContainer cssProperties={marginStyles}>
        <Section
          ref={ref}
          style={{
            ...backgroundStyles,
            ...paddingStyles,
          }}
          {...rest}
        >
          <Row>
            <Column>{headerContent}</Column>
          </Row>
          <Row>
            <Column
              style={columnStyle}
              className={css`
                ${mobileMediaQuery} {
                  display: block;
                  width: 100% !important;
                  padding-right: 0 !important;
                  padding-bottom: 48px;
                }
              `}
            >
              {section1Content}
            </Column>
            <Column
              style={columnStyle}
              className={css`
                ${mobileMediaQuery} {
                  display: block;
                  width: 100% !important;
                  padding-left: 0 !important;
                  padding-right: 0 !important;
                  padding-bottom: 48px;
                }
              `}
            >
              {section2Content}
            </Column>
            <Column
              style={columnStyle}
              className={css`
                ${mobileMediaQuery} {
                  display: block;
                  width: 100% !important;
                  padding-left: 0 !important;
                  padding-bottom: 0;
                }
              `}
            >
              {section3Content}
            </Column>
          </Row>
          <Row>
            <Column>{footerContent}</Column>
          </Row>
        </Section>
      </SectionContainer>
    );
  },
);

FeatureBlock3.displayName = "FeatureBlock3";
